import * as React from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { observer } from "mobx-react";
import { Tabs, Row, Button, Form, FormInstance, message } from "antd";
import { OrderParty } from "./OrderParty";
import { contactStore } from "Pages/UserSettings/Stores/ContactStore";
import { filePrintStore } from "../FilePrintStore";
import { InvoiceRecipient } from "./InvoiceRecipient";
import { contactsStore as allUsersContactStore } from "Stores/ContactsStore";
import { RecipientDelivery } from "./RecipientDelivery";
import { Files } from "./FilesList";
import { PlotProvider } from "./PlotProvider";
import { groupListStore } from "Pages/Project/Groups/Stores/GroupsStore";
import { userInfoStore, projectsDataStore } from "Stores";
import { Confirmation } from "./Confirmation";
import { notificationSuccess } from "utils/libHelpers/AntdNotification";
import { API_URL } from "General/APIEndpoints";
import { API } from "General/API";
import { Loader } from "Components/General/Loader";
import { ModalComponentProps } from "Components/General/Modal/ModalContext";
import { fileDetailsStore } from "Components/FileDetails/FileDetailsStore";
import { RouterStore } from "Config/CustomBrowserRouter";

interface PrintFormViewProps extends WithTranslation, ModalComponentProps {
  files: API_FileDataEntity[];
  onSaveHandler?: (values) => void;
}

interface PrintFormViewState {
  activeKey: string;
  isActiveTabs: string[];
  noCopies: { [filename: string]: number };
  loading: boolean;
  initialLoading: boolean;
  formData: API_PrintJob;
  formHasErrors: boolean;
}

export const PrintForm = withTranslation([I18N_NS.PROJECT, I18N_NS.GENERAL])(
  observer(
    class PrintFormView extends React.Component<PrintFormViewProps, PrintFormViewState> {
      private tabsList = ["Ordering party", "Invoice recipient", "Recipients", "Files", "Plot service", "Confirmation"];

      formRef: React.RefObject<FormInstance> = React.createRef();
      constructor(props: PrintFormViewProps) {
        super(props);
        this.state = {
          activeKey: "Ordering party",
          isActiveTabs: ["Ordering party"],
          noCopies: {},
          loading: false,
          initialLoading: true,
          formData: { providerId: filePrintStore.plotProviders[0]?.id },
          formHasErrors: false,
        };
      }

      componentDidMount() {
        (async () => {
          filePrintStore.init(this.props.files);
          await contactStore.fetch();
          await this.setupPrintJobProviders();
          await allUsersContactStore.fetch();
          if (!groupListStore.list.length) {
            await groupListStore.fetch();
          }
          this.setState({ initialLoading: false });
        })();
      }

      // setup initial provider
      setupPrintJobProviders = async () => {
        await filePrintStore.getPlotProviders();
        const firstProviderId = filePrintStore.plotProviders[0]?.id;
        this.setState({ formData: { providerId: firstProviderId } });
        this.formRef.current?.setFieldValue("providerId", firstProviderId);
      };

      validateFiles = (files: API_PrintJobFile[]) => {
        let result: boolean = false;
        files.forEach((file) => {
          if (Object.keys(file).some((field) => !file[field])) {
            result = true;
          }
        });
        return result;
      };

      handleTabChange = (activeKey: string) => {
        const { t } = this.props;
        this.formRef.current.validateFields().then((values) => {
          let error;
          // Check if all fields are filled for each file
          // if no - manually identify the error as true
          if (activeKey === "Plot service" && values.files?.length) {
            error = this.validateFiles(values.files);
          }

          if (!error) {
            this.setState({ activeKey });
            this.handleTabActivated(activeKey);
          } else {
            message.error(t("Please fill all fields"));
          }
        });
      };

      handleManualTabChange = (tab: string, moveForward: boolean = true) => {
        const tabsList = this.getTabsList();
        const currentTab = tabsList.indexOf(tab);
        const nextTab = moveForward ? tabsList[currentTab + 1] : tabsList[currentTab - 1];

        this.handleTabChange(nextTab);
      };

      handleTabActivated = (tab: string) => {
        const isTabDefined = this.state.isActiveTabs.includes(tab);

        if (!isTabDefined) {
          this.setState({ isActiveTabs: [...this.state.isActiveTabs, tab] });
        }
      };

      handleCancel = () => {
        fileDetailsStore.clear();
        this.props.hideModal();
      };

      renderPrevButton = (currentKey) => {
        const { t } = this.props;

        return currentKey !== "Ordering party" ? (
          <Button onClick={() => this.handleManualTabChange(this.state.activeKey, false)}>{t("Back")}</Button>
        ) : (
          <Button onClick={this.handleCancel}>{t(`${I18N_NS.GENERAL}:::Cancel`)}</Button>
        );
      };

      renderNextButton = (currentKey) => {
        const { t } = this.props;

        return currentKey !== "Confirmation" ? (
          <Button
            type="primary"
            onClick={() => this.handleManualTabChange(this.state.activeKey)}
            disabled={this.state.formHasErrors}
          >
            {t("Next")}
          </Button>
        ) : (
          <Button type="primary" onClick={() => this.handleSubmit()} htmlType="submit" loading={this.state.loading}>
            {t("Save")}
          </Button>
        );
      };

      getTabsList() {
        if (filePrintStore.usedForDisitibutionMatrix) {
          const deactivatedLables = { Recipients: true };
          return this.tabsList.filter((tab) => !deactivatedLables[tab]);
        }
        return this.tabsList;
      }

      get tabs() {
        const { t } = this.props;
        const { additionalInvoiceRecipientId, hasAdditionalInvoiceRecipient, defaultInvoiceRecipient } =
          projectsDataStore.currentProject.printJobModuleSettings;
        const tabsConfig = [
          {
            key: "Ordering party",
            label: t("Ordering party"),
            children: <OrderParty data={contactStore.data} />,
            disabled: !this.state.isActiveTabs.includes("Ordering party"),
          },
          {
            key: "Invoice recipient",
            label: t("Invoice recipient"),
            children: (
              <InvoiceRecipient
                isAdditional={hasAdditionalInvoiceRecipient}
                additionalId={additionalInvoiceRecipientId}
                defaultId={defaultInvoiceRecipient}
                userId={userInfoStore.id}
                isRestricted={userInfoStore.isRestrictedUser}
                form={this.formRef}
              />
            ),
            disabled: !this.state.isActiveTabs.includes("Invoice recipient"),
          },
          {
            key: "Recipients",
            label: t("Recipients"),
            children: <RecipientDelivery />,
            disabled: !this.state.isActiveTabs.includes("Recipients"),
          },
          {
            key: "Files",
            label: t("Files"),
            children: <Files noCopies={this.state.noCopies} files={filePrintStore.data.slice()} />,
            disabled: !this.state.isActiveTabs.includes("Files"),
          },
          {
            key: "Plot service",
            label: t("Plot service"),
            children: <PlotProvider plotProviders={filePrintStore.plotProviders} />,
            disabled: !this.state.isActiveTabs.includes("Plot service"),
          },
          {
            key: "Confirmation",
            label: t("Confirmation"),
            children: <Confirmation form={this.formRef} formData={this.state.formData} />,
            destroyOnHidden: true,
            disabled: !this.state.isActiveTabs.includes("Confirmation"),
          },
        ];

        if (filePrintStore.usedForDisitibutionMatrix) {
          const deactivatedLables = { Recipients: true };
          return tabsConfig.filter((conf) => !deactivatedLables[conf.label]);
        }
        return tabsConfig;
      }

      handleSubmit = async () => {
        const { t } = this.props;
        const values = this.formRef.current.getFieldsValue();
        // deleted firstName since we don't have that type and don't use it on BE
        values?.recipients.map((item) => delete item?.firstName);
        values.invoiceRecipient.firstName = undefined;
        // if (!err) {
        if (values.deliveryDate) {
          values.deliveryDate = +values.deliveryDate.format("x");
        }
        try {
          if (this.props.onSaveHandler) {
            this.props.onSaveHandler(values);
            return;
          }
          this.setState({ loading: true, noCopies: {} });
          const response = await API.newData(API_URL.PRINTJOB.createPrintJob(projectsDataStore.projectId), values);
          // DEV-3627 throw custom error for case when back-end returns error with 200 status
          if (response && (response as API_PrintjobError).type === "NO_COPIES") {
            const error = { response: { data: response } };
            throw error;
          } else if (response) {
            notificationSuccess(t("Success"), t("New printjob created"));
            this.handleCancel();
            if (!userInfoStore.isRestrictedUser) {
              RouterStore.push(`/project/${projectsDataStore.projectId}/reports`);
            }
          }
        } catch (err) {
          // eslint-disable-next-line max-len
          // DEV-3627 error handling for exceeded limit of copies - move to Files tab and handle error object there
          if (err.response && err.response.data && (err.response.data as API_PrintjobError).type === "NO_COPIES") {
            this.setState({ activeKey: "Files", noCopies: err.response.data.noCopies });
          }
          throw err;
        } finally {
          this.setState({ loading: false });
        }
        // } else {
        //   window.scrollTo(0, 0);
        // }
      };

      checkFormValid = () => {
        const hasErrors = this.formRef?.current?.getFieldsError().some(({ errors }) => errors.length > 0);
        this.setState({ formHasErrors: hasErrors });
      };

      render() {
        if (this.state.initialLoading) {
          return <Loader />;
        }
        return (
          <Form
            layout="vertical"
            ref={this.formRef}
            className="ant-form-item-grey-label ant-form-remove-nav"
            onValuesChange={(_, formData) => {
              console.log("formData", formData);
              this.checkFormValid();
              this.setState({ formData: { ...this.state.formData, ...formData } });
            }}
          >
            <Tabs
              activeKey={this.state.activeKey}
              onChange={this.handleTabChange}
              tabPosition="left"
              items={this.tabs}
            />
            <Row justify="space-between" style={{ marginTop: "20px" }}>
              {this.renderPrevButton(this.state.activeKey)}
              {this.renderNextButton(this.state.activeKey)}
            </Row>
          </Form>
        );
      }
    }
  )
);
